import { IonItem, IonLabel, PickerColumn, useIonPicker } from '@ionic/react';
import { CustomIconProps, RoundedSquareIcon } from './Icon';
import './ItemWithPicker.scss';
import { map, range, rangeStep, toString } from 'lodash/fp';
import { hoursToDuration } from '../utils/date';

type PickerProps = {
    value: number;
    title: string;
    iconComponent?: React.FC<CustomIconProps>;
    iconProps: CustomIconProps;
    disabled?: boolean;
    backgroundColor?: string;
    onChange: (value: number) => void;
};

export const ItemWithHoursPicker: React.FC<PickerProps & { step?: number }> = ({ step = 15, ...pickerProps }) => {
    const valueToDisplay = (value: number) => hoursToDuration(value).toFormat('h:mm');

    const getColumns = (value: number) => [
        {
            name: 'hours',
            options: map((i) => ({
                text: toString(i),
                value: i,
            }))(range(0, 51)),
            selectedIndex: Math.floor(value),
        },
        {
            name: 'minutes',
            options: map((i: number) => ({
                text: toString(i),
                value: i / 60,
            }))(rangeStep(step, 0, 60)),
            selectedIndex: Math.round(((value % 1) * 60) / step),
        },
    ];

    return (
        <ItemWithPicker
            pickerProps={pickerProps}
            valueToDisplay={valueToDisplay}
            getColumns={getColumns}
            handleChange={(selected) => pickerProps.onChange(selected.hours.value + selected.minutes.value)}
        />
    );
};

export const ItemWithNumberPicker: React.FC<PickerProps & { max?: number }> = (pickerProps) => {
    let max = Math.max(pickerProps.max || 50, pickerProps.value);

    return (
        <ItemWithPicker
            pickerProps={pickerProps}
            getColumns={(value: number) => [
                {
                    name: 'options',
                    options: map((i) => ({
                        text: toString(i),
                        value: i,
                    }))(range(0, max + 1)),
                    selectedIndex: value,
                },
            ]}
            handleChange={(selected) => pickerProps.onChange(selected.options.value)}
        />
    );
};

const ItemWithPicker: React.FC<{
    pickerProps: PickerProps;
    valueToDisplay?: (value: number) => string;
    getColumns: (value: number) => PickerColumn[];
    handleChange?: (selected: any) => void;
}> = ({
    pickerProps: {
        value,
        title,
        iconProps,
        disabled,
        backgroundColor = 'white',
        iconComponent = RoundedSquareIcon,
        onChange,
    },
    valueToDisplay,
    getColumns,
    handleChange,
}) => {
    const [present] = useIonPicker();

    const openPicker = async () => {
        present({
            buttons: [
                {
                    text: 'Annuler',
                },
                {
                    text: 'Valider',
                    handler: (selected) => {
                        handleChange ? handleChange(selected) : onChange(selected.options.value);
                    },
                },
            ],
            columns: getColumns(value),
        });
    };

    return (
        <IonItem className="hours-picker-v2" style={{ '--background': backgroundColor }}>
            {iconComponent(iconProps)}
            <IonLabel className={`pl-1 ${disabled ? 'item-interactive-disabled' : ''}`}>{title}</IonLabel>
            <button className="btn btn-light" onClick={openPicker} disabled={disabled}>
                {valueToDisplay?.(value) ?? value}
            </button>
        </IonItem>
    );
};

export default ItemWithPicker;
